import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MapPin, Search, ShieldCheck, Star } from "lucide-react";
import { hero } from "../content/site";

const PHASES = { TYPING: "typing", RESULTS: "results", HOLD: "hold" };

export default function HeroVisual() {
  const [phase, setPhase] = useState(PHASES.TYPING);
  const [typed, setTyped] = useState("");

  useEffect(() => {
    const query = hero.demo.query;
    let i = 0;
    let timeout;

    function typeNext() {
      if (i <= query.length) {
        setTyped(query.slice(0, i));
        i += 1;
        timeout = setTimeout(typeNext, 90);
      } else {
        timeout = setTimeout(() => setPhase(PHASES.RESULTS), 400);
      }
    }

    if (phase === PHASES.TYPING) {
      setTyped("");
      i = 0;
      timeout = setTimeout(typeNext, 300);
    } else if (phase === PHASES.RESULTS) {
      timeout = setTimeout(() => setPhase(PHASES.HOLD), 1800);
    } else if (phase === PHASES.HOLD) {
      timeout = setTimeout(() => setPhase(PHASES.TYPING), 2600);
    }

    return () => clearTimeout(timeout);
  }, [phase]);

  const showResults = phase === PHASES.RESULTS || phase === PHASES.HOLD;

  return (
    <div className="relative mx-auto w-full max-w-md">
      {/* Ambient glow */}
      <div className="absolute -inset-10 -z-10 rounded-[3rem] bg-gradient-to-br from-primary/25 via-secondary/10 to-transparent blur-3xl" />

      {/* Main search demo card */}
      <div className="relative rounded-[28px] border border-white/60 bg-white/95 p-5 shadow-navy backdrop-blur-sm sm:p-6">
        <p className="mb-3 text-xs font-bold tracking-wide text-navy/40 uppercase">
          {hero.demo.disclaimer}
        </p>

        <p className="mb-2 text-lg font-extrabold text-navy">
          {hero.demo.prompt}
        </p>

        <div className="flex items-center gap-2 rounded-2xl border-2 border-navy/10 bg-[var(--color-bg-soft)] px-4 py-3">
          <Search size={18} className="shrink-0 text-primary" />
          <span className="text-base font-semibold text-navy">
            {typed}
            <motion.span
              animate={{ opacity: [1, 0] }}
              transition={{ duration: 0.6, repeat: Infinity, repeatType: "reverse" }}
              className="ml-0.5 inline-block w-[2px] translate-y-[2px] bg-primary align-middle h-4"
            />
          </span>
        </div>

        <AnimatePresence mode="popLayout">
          {showResults && (
            <motion.div
              className="mt-4 flex flex-col gap-2.5"
              initial="hidden"
              animate="show"
              exit="hidden"
            >
              {hero.demo.results.map((r, idx) => (
                <motion.div
                  key={r.distance}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0 }}
                  transition={{ delay: idx * 0.12, duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
                  className="flex items-center justify-between rounded-xl border border-navy/8 bg-white px-3.5 py-2.5 shadow-sm"
                >
                  <div className="flex items-center gap-2.5">
                    <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-[var(--color-bg-light-orange)]">
                      <MapPin size={15} className="text-primary" />
                    </span>
                    <div>
                      <p className="text-sm font-bold text-navy">{r.distance} away</p>
                      <p className="text-xs font-semibold text-success">{r.status}</p>
                    </div>
                  </div>
                  <span className="text-sm font-extrabold text-navy">{r.price}</span>
                </motion.div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Floating status card */}
      <motion.div
        animate={{ y: [0, -12, 0] }}
        transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
        className="absolute -right-4 -bottom-8 w-56 rounded-2xl border border-white/60 bg-white p-4 shadow-card sm:-right-10"
      >
        <div className="flex items-center justify-between">
          <p className="text-sm font-extrabold text-navy">{hero.statusCard.title}</p>
          <span className="relative flex h-2.5 w-2.5">
            <span className="absolute inline-flex h-full w-full animate-pulse-slow rounded-full bg-success" />
            <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-success" />
          </span>
        </div>
        <p className="mt-1 text-xs font-semibold text-navy/50">{hero.statusCard.spaces}</p>
        <div className="mt-3 flex items-center justify-between text-xs">
          <span className="font-semibold text-navy/60">{hero.statusCard.distance}</span>
          <span className="font-extrabold text-primary">{hero.statusCard.price}</span>
        </div>
        <div className="mt-2 inline-flex items-center gap-1 rounded-full bg-[var(--color-bg-light-orange)] px-2.5 py-1 text-[11px] font-bold text-primary">
          <ShieldCheck size={12} />
          {hero.statusCard.tag}
        </div>
      </motion.div>

      {/* Floating rating chip */}
      <motion.div
        animate={{ y: [0, 10, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
        className="absolute -top-6 -left-6 flex items-center gap-1.5 rounded-2xl border border-white/60 bg-white px-3.5 py-2.5 shadow-card sm:-left-10"
      >
        <Star size={16} className="fill-primary text-primary" />
        <span className="text-sm font-extrabold text-navy">4.8</span>
        <span className="text-xs font-semibold text-navy/40">rating</span>
      </motion.div>
    </div>
  );
}
